import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Copy, Link2, Loader2, Search, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/shared/DatePicker';
import { isAfterWarsawToday } from '@/utils/warsawDate';
import { useCombinedPaymentsApi } from '@/services/api';
import { formatPLN } from '@/utils/currency';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import type {
  CombinedPaymentCombination,
  CombinedPaymentSearchResult,
  SalesCombinedPaymentCustomer,
} from '@ip/shared-types';

/** Mirrors the backend SALES_TOLERANCE_GROSZE: |difference| up to 1.00. */
const SALES_TOLERANCE = 1.0;

interface MatchIncomingPaymentDialogProps {
  companyNip: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a combination was confirmed — reload the list + receivables. */
  onLinked: () => void;
}

/** Identity key for a (customer) group — NIP when present, else name. */
function customerKey(c: SalesCombinedPaymentCustomer): string {
  return c.customerNip ?? `name:${c.customerName ?? ''}`;
}

function comboToClipboard(combo: CombinedPaymentCombination): string {
  const lines = combo.invoices.map((invoice) =>
    [
      invoice.invoiceNumber ?? '—',
      invoice.saleDate ?? invoice.issueDate ?? '—',
      invoice.outstanding != null ? invoice.outstanding.toFixed(2) : '—',
    ].join('\t'),
  );
  lines.push(`${t('invoices.combinedPayment.sumLabel')}\t\t${combo.sum.toFixed(2)}`);
  return lines.join('\n');
}

/**
 * "Dopasuj wpłatę" (sales): one customer transfer — often foreign, with no
 * invoice numbers in the title — covering several sale invoices. The
 * accountant picks the customer + currency and types in the transfer's date
 * and amount; the backend proposes subsets of that customer's OPEN invoices
 * whose outstanding balances sum to the amount (differences up to 1.00 in
 * the currency are acceptable and shown). Confirming registers a payment on
 * every invoice for its full outstanding balance via the standard
 * settlement flow.
 */
export function MatchIncomingPaymentDialog({
  companyNip,
  open,
  onOpenChange,
  onLinked,
}: MatchIncomingPaymentDialogProps) {
  const [customers, setCustomers] = useState<SalesCombinedPaymentCustomer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomerKey, setSelectedCustomerKey] = useState('');
  const [currency, setCurrency] = useState('');
  const [transactionDate, setTransactionDate] = useState<Date | null>(new Date());
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<CombinedPaymentSearchResult | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);

  const { listSalesMatchCustomers, searchSalesCombinations, createSalesCombinedPayment } =
    useCombinedPaymentsApi();

  // (Re)load customer options and reset the form each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSelectedCustomerKey('');
    setCurrency('');
    setTransactionDate(new Date());
    setAmount('');
    setNote('');
    setResult(null);
    setSelectedIndex(null);
    setCustomersLoading(true);
    listSalesMatchCustomers(companyNip)
      .then(setCustomers)
      .catch((error) => {
        toast.error(error instanceof Error ? error.message : t('errors.loadingFailed'));
        setCustomers([]);
      })
      .finally(() => setCustomersLoading(false));
  }, [open, companyNip, listSalesMatchCustomers]);

  // Distinct customers (a customer appears once even with several currencies).
  const customerOptions = useMemo(() => {
    const seen = new Map<string, SalesCombinedPaymentCustomer>();
    for (const c of customers) {
      if (!seen.has(customerKey(c))) seen.set(customerKey(c), c);
    }
    return [...seen.values()];
  }, [customers]);

  const selectedCustomer = customerOptions.find((c) => customerKey(c) === selectedCustomerKey);
  const currencyOptions = useMemo(
    () => customers.filter((c) => customerKey(c) === selectedCustomerKey),
    [customers, selectedCustomerKey],
  );

  // Auto-pick the currency when the customer has only one.
  useEffect(() => {
    setResult(null);
    setSelectedIndex(null);
    if (currencyOptions.length === 1) {
      setCurrency(currencyOptions[0].currency);
    } else {
      setCurrency('');
    }
  }, [selectedCustomerKey, currencyOptions]);

  const parsedAmount = amount === '' ? null : Number(amount);
  const amountInvalid = parsedAmount !== null && (!Number.isFinite(parsedAmount) || parsedAmount <= 0);
  const canSearch =
    !searching &&
    selectedCustomer !== undefined &&
    currency !== '' &&
    transactionDate !== null &&
    parsedAmount !== null &&
    !amountInvalid;

  const combinations: CombinedPaymentCombination[] = useMemo(
    () => (result ? (result.exact.length > 0 ? result.exact : result.approximate) : []),
    [result],
  );

  const handleSearch = useCallback(async () => {
    if (!selectedCustomer || !currency || !transactionDate || parsedAmount === null || amountInvalid) return;
    try {
      setSearching(true);
      setSelectedIndex(null);
      const data = await searchSalesCombinations(companyNip, {
        transactionDate: dayjs(transactionDate).format('YYYY-MM-DD'),
        amount: parsedAmount,
        currency,
        customerNip: selectedCustomer.customerNip ?? undefined,
        customerName: selectedCustomer.customerName ?? undefined,
      });
      setResult(data);
      // Preselect a single confirmable match — one click less.
      const list = data.exact.length > 0 ? data.exact : data.approximate;
      if (list.length === 1 && Math.abs(list[0].difference) <= SALES_TOLERANCE) setSelectedIndex(0);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('invoices.combinedPayment.searchError'));
      setResult(null);
    } finally {
      setSearching(false);
    }
  }, [companyNip, selectedCustomer, currency, transactionDate, parsedAmount, amountInvalid, searchSalesCombinations]);

  const handleCopy = useCallback(async (combo: CombinedPaymentCombination) => {
    try {
      await navigator.clipboard.writeText(comboToClipboard(combo));
      toast.success(t('invoices.combinedPayment.copied'));
    } catch {
      toast.error(t('invoices.combinedPayment.copyError'));
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    const combo = selectedIndex !== null ? combinations[selectedIndex] : undefined;
    if (!combo || !selectedCustomer || !currency || !transactionDate || parsedAmount === null) return;
    if (Math.abs(combo.difference) > SALES_TOLERANCE) return;
    try {
      setConfirming(true);
      await createSalesCombinedPayment(companyNip, {
        transactionDate: dayjs(transactionDate).format('YYYY-MM-DD'),
        amount: parsedAmount,
        currency,
        invoiceIds: combo.invoices.map((invoice) => invoice.id),
        note: note.trim() || undefined,
      });
      toast.success(
        t('invoices.combinedPayment.salesAcceptSuccess', {
          count: combo.invoices.length,
          amount: `${formatPLN(parsedAmount)} ${currency}`,
        }),
      );
      onOpenChange(false);
      onLinked();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('invoices.combinedPayment.acceptError'));
    } finally {
      setConfirming(false);
    }
  }, [selectedIndex, combinations, selectedCustomer, currency, transactionDate, parsedAmount, note, companyNip, createSalesCombinedPayment, onOpenChange, onLinked]);

  const selectedCombo = selectedIndex !== null ? combinations[selectedIndex] : undefined;
  const canConfirm =
    !confirming && selectedCombo !== undefined && Math.abs(selectedCombo.difference) <= SALES_TOLERANCE;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('invoices.combinedPayment.salesDialogTitle')}</DialogTitle>
          <DialogDescription>{t('invoices.combinedPayment.salesDialogDescription')}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">
              {t('invoices.combinedPayment.customerLabel')}
            </Label>
            <Select
              value={selectedCustomerKey}
              onValueChange={setSelectedCustomerKey}
              disabled={customersLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    customersLoading
                      ? t('common.loading')
                      : customerOptions.length === 0
                        ? t('invoices.combinedPayment.noCustomers')
                        : t('invoices.combinedPayment.customerPlaceholder')
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {customerOptions.map((c) => (
                  <SelectItem key={customerKey(c)} value={customerKey(c)}>
                    {c.customerName || c.customerNip || '—'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">
              {t('invoices.combinedPayment.currencyLabel')}
            </Label>
            <Select value={currency} onValueChange={setCurrency} disabled={!selectedCustomer}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {currencyOptions.map((c) => (
                  <SelectItem key={c.currency} value={c.currency}>
                    {t('invoices.combinedPayment.currencyOption', {
                      currency: c.currency,
                      count: c.openCount,
                      outstanding: formatPLN(c.outstanding),
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">
              {t('invoices.combinedPayment.transferDate')}
            </Label>
            <DatePicker
              value={transactionDate}
              onChange={setTransactionDate}
              disabledDate={isAfterWarsawToday}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">
              {t('invoices.combinedPayment.amountCurrency', { currency: currency || '—' })}
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              placeholder="123.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSearch) handleSearch();
              }}
              aria-invalid={amountInvalid}
            />
          </div>
        </div>

        <Button onClick={handleSearch} disabled={!canSearch}>
          {searching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          {t('invoices.combinedPayment.searchButton')}
        </Button>

        {result && combinations.length === 0 && (
          <p className="text-muted-foreground text-sm">
            {t('invoices.combinedPayment.salesNoMatches', { count: result.candidateCount })}
          </p>
        )}

        {combinations.length > 0 && (
          <div className="space-y-2">
            {combinations.map((combo, index) => {
              const confirmable = Math.abs(combo.difference) <= SALES_TOLERANCE;
              return (
                <div
                  key={index}
                  role="button"
                  tabIndex={0}
                  aria-disabled={!confirmable}
                  onClick={() => confirmable && setSelectedIndex(index)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && confirmable) setSelectedIndex(index);
                  }}
                  className={cn(
                    'rounded-md border p-3 transition-colors',
                    confirmable ? 'cursor-pointer' : 'opacity-70',
                    selectedIndex === index ? 'border-primary bg-primary/5' : confirmable && 'hover:bg-accent/50',
                  )}
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">
                      {t('invoices.combinedPayment.comboLabel', { count: combo.invoices.length })}
                    </span>
                    <div className="flex items-center gap-2">
                      {combo.difference !== 0 && (
                        <span
                          className={cn(
                            'flex items-center gap-1 text-xs font-medium',
                            confirmable
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-destructive',
                          )}
                        >
                          {!confirmable && <TriangleAlert className="size-3.5" />}
                          {t('invoices.combinedPayment.difference')}: {formatPLN(combo.difference)} {currency}
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(combo);
                        }}
                      >
                        <Copy className="size-3.5" />
                        {t('invoices.combinedPayment.copy')}
                      </Button>
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {combo.invoices.map((invoice) => (
                        <tr key={invoice.id} className="border-border/50 border-b last:border-0">
                          <td className="py-1 pr-2 font-mono text-xs">{invoice.invoiceNumber ?? '—'}</td>
                          <td className="py-1 pr-2 tabular-nums">
                            {invoice.saleDate ?? invoice.issueDate ?? '—'}
                          </td>
                          <td className="py-1 text-right tabular-nums">
                            {invoice.outstanding != null ? formatPLN(invoice.outstanding) : '—'}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={2} className="py-1 pr-2 text-right text-xs font-medium">
                          {t('invoices.combinedPayment.sumLabel')}
                        </td>
                        <td className="py-1 text-right font-semibold tabular-nums">
                          {formatPLN(combo.sum)} {currency}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {!confirmable && (
                    <p className="text-muted-foreground mt-1 text-xs">
                      {t('invoices.combinedPayment.diffTooBig')}
                    </p>
                  )}
                </div>
              );
            })}
            {result?.truncated && (
              <p className="text-muted-foreground text-xs">{t('invoices.combinedPayment.truncated')}</p>
            )}
            {selectedCombo && selectedCombo.difference !== 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                {t('invoices.combinedPayment.salesDiffInfo', {
                  diff: `${formatPLN(selectedCombo.difference)} ${currency}`,
                })}
              </p>
            )}
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">
                {t('invoices.combinedPayment.noteLabel')}
              </Label>
              <Input
                placeholder={t('invoices.combinedPayment.salesNotePlaceholder')}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={1000}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={confirming}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            {confirming ? <Loader2 className="size-4 animate-spin" /> : <Link2 className="size-4" />}
            {t('invoices.combinedPayment.salesAccept')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
